import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import {
  DeadletteredSalesforceJob,
  GetDeadletteredSalesforceJobsFn,
  RequeueDeadletteredSalesforceJobFn,
  SalesforceSyncRecordType,
  isAdmin,
  isInternalUser,
} from '@portal-workspace/grow-shared-library';
import { MessageBoxComponent } from '../message-box/message-box.component';
import { PortalHotToastService } from '../portal-hot-toast-component/hot-toast.service';
import { getUser, setupUntilDestroy } from '../component-utils';

/** Shown on a record's details page while Salesforce is missing writes the outbox gave up on. */
@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'salesforce-sync-failed',
  templateUrl: './salesforce-sync-failed.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MessageBoxComponent, MatButtonModule, DatePipe],
  // Salesforce errors can echo a long unbroken rejected value, which would otherwise push Retry out of view.
  styles: [`.sync-failed-job { overflow-wrap: anywhere; word-break: break-word; }`],
})
export class SalesforceSyncFailedComponent implements OnInit {
  @Input({ required: true }) recordType!: SalesforceSyncRecordType;
  @Input({ required: true }) recordId!: number;
  @Input({ required: true }) getDeadletteredSalesforceJobsFn!: GetDeadletteredSalesforceJobsFn;
  @Input({ required: true }) requeueDeadletteredSalesforceJobFn!: RequeueDeadletteredSalesforceJobFn;

  subscriptions: Subscription[] = [];
  jobs: DeadletteredSalesforceJob[] = [];
  canRetry = isAdmin(getUser());
  retrying = false;

  constructor(
    private toastService: PortalHotToastService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    setupUntilDestroy(this);
    this.loadJobs();
  }

  private loadJobs() {
    if (!isInternalUser(getUser())) {
      return;
    }
    this.subscriptions.push(this.getDeadletteredSalesforceJobsFn(this.recordType, this.recordId).subscribe(jobs => {
      this.jobs = jobs;
      this.changeDetectorRef.markForCheck();
    }));
  }

  onRetry(job: DeadletteredSalesforceJob) {
    this.retrying = true;
    this.subscriptions.push(this.requeueDeadletteredSalesforceJobFn(job.correlationId).subscribe({
      next: () => {
        this.retrying = false;
        this.changeDetectorRef.markForCheck();
        this.toastService.quickSuccessToast('Salesforce sync requeued');
        this.loadJobs();
      },
      error: (err) => {
        this.retrying = false;
        this.changeDetectorRef.markForCheck();
        this.toastService.quickErrorToast(err?.error?.message ?? 'Unable to requeue the Salesforce sync');
      },
    }));
  }
}
